import { useEffect, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import type { ReferrerState } from '../utils/navigation';

export type Referrer = { url: string; label?: string };

const readReferrer = (state: unknown): Referrer | null => {
  const { referrer, referrerLabel } = (state as ReferrerState | null) ?? {};
  return referrer ? { url: referrer, label: referrerLabel } : null;
};

/**
 * The caller that linked here (see `referrerState`), or null.
 *
 * List pages rewrite their own URL with `replace` navigations (filters, the
 * `?id=` drawer, tabs) that drop router state, so the referrer is captured on
 * arrival and kept across REPLACE. Any PUSH/POP without referrer state (menu
 * click, browser back/forward) is a fresh arrival and clears it.
 */
export function useReferrer(): Referrer | null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [referrer, setReferrer] = useState<Referrer | null>(() => readReferrer(location.state));

  useEffect(() => {
    const next = readReferrer(location.state);
    if (next) {
      setReferrer(next);
      return;
    }
    if (navigationType !== 'REPLACE') setReferrer(null);
    // location.key changes on every navigation; state/type are read with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return referrer;
}
